import { useEffect, useState } from "react";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  ResponsiveContainer, Tooltip, RadialBarChart, RadialBar, CartesianGrid, Cell,
} from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";
import { api } from "@/lib/api";
import { formatValue, formatChange } from "@/lib/format";

const CHART_COLOR = "hsl(221 83% 53%)";
const GRID = "hsl(240 4% 16%)";

function Sparkline({ data, positive }) {
  const chart = data.map((v, i) => ({ i, v }));
  const color = positive ? "hsl(142 71% 45%)" : "hsl(0 72% 51%)";
  return (
    <ResponsiveContainer width="100%" height={40}>
      <LineChart data={chart} margin={SPARK_MARGIN}>
        <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.75} dot={false} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function KpiCard({ metric }) {
  if (!metric) return null;
  const up = metric.change >= 0;
  const good = metric.higher_better ? up : !up;
  return (
    <div className="h-full border border-border bg-card rounded-md p-4 flex flex-col justify-between hover:border-primary/50 transition-colors" data-testid={`kpi-card-${metric.key}`}>
      <div className="flex items-start justify-between">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">{metric.label}</span>
        <span className={`inline-flex items-center gap-0.5 text-xs font-mono ${good ? "text-[hsl(142_71%_55%)]" : "text-destructive"}`} data-testid={`kpi-change-${metric.key}`}>
          {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {formatChange(metric.change)}
        </span>
      </div>
      <div className="mt-3 mb-2">
        <div className="tnum text-3xl font-bold tracking-tight" data-testid={`kpi-value-${metric.key}`}>
          {formatValue(metric.value, metric.format, metric.unit)}
        </div>
      </div>
      <Sparkline data={metric.sparkline} positive={good} />
    </div>
  );
}

function useSeries(url, params, tick) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    api.get(url, { params }).then((r) => { if (alive) { setData(r.data); setLoading(false); } }).catch(() => setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [url, JSON.stringify(params), tick]);
  return { data, loading };
}

function ChartShell({ title, children, testid, override }) {
  return (
    <div className="h-full border border-border bg-card rounded-md p-4 flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
          {title}
          {override && <span className="text-[9px] px-1 py-0.5 rounded bg-primary/20 text-primary tracking-normal" title="Widget-level filter active">FILTERED</span>}
        </span>
        <span className="h-1.5 w-1.5 rounded-full bg-[hsl(142_71%_45%)] animate-pulse" title="live" />
      </div>
      <div className="flex-1 min-h-[180px]">{children}</div>
    </div>
  );
}

const tooltipStyle = {
  background: "hsl(240 6% 8%)", border: "1px solid hsl(240 4% 16%)",
  borderRadius: 6, fontSize: 12, fontFamily: "JetBrains Mono",
};
const CHART_MARGIN = { top: 5, right: 8, left: -18, bottom: 0 };
const SPARK_MARGIN = { top: 4, bottom: 4, left: 0, right: 0 };
const AXIS_TICK = { fontSize: 10, fill: "hsl(240 5% 65%)" };
const BAR_X_TICK = { fontSize: 9, fill: "hsl(240 5% 65%)" };
const BAR_RADIUS = [4, 4, 0, 0];
const BAR_CURSOR = { fill: "hsl(240 4% 16% / 0.4)" };
const GAUGE_BG = { fill: "hsl(240 4% 16%)" };

export function LineWidget({ widget, filters, tick, catalog, sharedToken, dataBase }) {
  const f = { ...filters, ...(widget.filters || {}) };
  const base = sharedToken ? `/shared/${sharedToken}` : (dataBase || "/metrics");
  const { data } = useSeries(`${base}/series`, { metric: widget.metric, ...f }, tick);
  const label = catalog[widget.metric]?.label || widget.metric;
  return (
    <ChartShell title={widget.title || label} testid={`chart-line-${widget.metric}`} override={!!widget.filters}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
          <XAxis dataKey="date" tick={AXIS_TICK} tickFormatter={(d) => d.slice(5)} minTickGap={30} />
          <YAxis tick={AXIS_TICK} width={48} />
          <Tooltip contentStyle={tooltipStyle} />
          <Line type="monotone" dataKey="value" stroke={CHART_COLOR} strokeWidth={2} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </ChartShell>
  );
}

export function AreaWidget({ widget, filters, tick, catalog, sharedToken, dataBase }) {
  const f = { ...filters, ...(widget.filters || {}) };
  const base = sharedToken ? `/shared/${sharedToken}` : (dataBase || "/metrics");
  const { data } = useSeries(`${base}/series`, { metric: widget.metric, ...f }, tick);
  const label = catalog[widget.metric]?.label || widget.metric;
  return (
    <ChartShell title={widget.title || label} testid={`chart-area-${widget.metric}`} override={!!widget.filters}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={CHART_MARGIN}>
          <defs>
            <linearGradient id={`g-${widget.id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART_COLOR} stopOpacity={0.4} />
              <stop offset="100%" stopColor={CHART_COLOR} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
          <XAxis dataKey="date" tick={AXIS_TICK} tickFormatter={(d) => d.slice(5)} minTickGap={30} />
          <YAxis tick={AXIS_TICK} width={48} />
          <Tooltip contentStyle={tooltipStyle} />
          <Area type="monotone" dataKey="value" stroke={CHART_COLOR} strokeWidth={2} fill={`url(#g-${widget.id})`} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </ChartShell>
  );
}

const BAR_COLORS = ["hsl(221 83% 53%)", "hsl(142 71% 45%)", "hsl(45 93% 47%)", "hsl(280 65% 60%)"];

export function BarWidget({ widget, filters, tick, catalog, sharedToken, dataBase }) {
  const by = widget.by || "region";
  const f = { ...filters, ...(widget.filters || {}) };
  const base = sharedToken ? `/shared/${sharedToken}` : (dataBase || "/metrics");
  const { data } = useSeries(`${base}/breakdown`, { metric: widget.metric, range: f.range, by }, tick);
  const label = catalog[widget.metric]?.label || widget.metric;
  return (
    <ChartShell title={widget.title || label} testid={`chart-bar-${widget.metric}`} override={!!widget.filters}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
          <XAxis dataKey="name" tick={BAR_X_TICK} />
          <YAxis tick={AXIS_TICK} width={48} />
          <Tooltip contentStyle={tooltipStyle} cursor={BAR_CURSOR} />
          <Bar dataKey="value" radius={BAR_RADIUS}>
            {data.map((d, i) => <Cell key={d.name ?? i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartShell>
  );
}

export function GaugeWidget({ widget, summaryMap, catalog }) {
  const m = summaryMap[widget.metric];
  const label = catalog[widget.metric]?.label || widget.metric;
  const value = m ? m.value : 0;
  const max = widget.metric === "uptime" ? 100 : Math.max(value * 1.3, 1);
  const pct = Math.min((value / max) * 100, 100);
  const color = pct > 66 ? "hsl(142 71% 45%)" : pct > 33 ? "hsl(45 93% 47%)" : "hsl(0 72% 51%)";
  const data = [{ name: "v", value: pct, fill: color }];
  return (
    <ChartShell title={label} testid={`chart-gauge-${widget.metric}`}>
      <div className="relative h-full flex items-center justify-center">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart innerRadius="70%" outerRadius="100%" data={data} startAngle={220} endAngle={-40}>
            <RadialBar background={GAUGE_BG} dataKey="value" cornerRadius={8} isAnimationActive={false} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="tnum text-2xl font-bold">{m ? formatValue(m.value, m.format, m.unit) : "—"}</span>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">current</span>
        </div>
      </div>
    </ChartShell>
  );
}

export function Widget(props) {
  const { widget } = props;
  if (widget.type === "kpi") return <KpiCard metric={props.summaryMap[widget.metric]} />;
  if (widget.type === "line") return <LineWidget {...props} />;
  if (widget.type === "area") return <AreaWidget {...props} />;
  if (widget.type === "bar") return <BarWidget {...props} />;
  if (widget.type === "gauge") return <GaugeWidget {...props} />;
  return null;
}
